/**
 * ტესტების აუდიო — the review page for every listening recording
 * (Tornike, 2026-09-26: "a page for reviewing … all the audios with their
 * texts").
 *
 * Every test's clip with the exact script it was recorded from, the voice
 * (Sarah for women, Jarnathan for men) and the length. He marks each clip
 * კარგია / პრობლემაა with an optional note; the marks stay in this browser,
 * and "დააკოპირე შენიშვნები" puts every problem into one message he can send.
 *
 * Route: /exam-audio-review (public, no auth — like /test-review). Plays the
 * same files the test player plays (audioUrl). QA tool; remove after launch.
 *
 * 2026-09-28: a level picker (opens on the new exam-level 81–100) and the
 * pace of each clip in words a minute (the real exam's recordings run ~150).
 * Marks moved to a v2 key: the old tests 81–100 became 101–120, so v1 marks
 * for 81–100 follow their clips (+20) instead of landing on the new tests.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Container,
  Heading,
  Screen,
  SegmentedControl,
  Skeleton,
  Stack,
  Text,
  Textarea,
} from '@fluent/ui';
import type { ExamTest } from '@fluent/types';
import { audioUrl } from '../audio/audioUrl.js';
import { EXAM_LEVELS, EXAM_TEST_COUNT, loadExamTest, testIdOf } from '../exam/catalog.js';
import './ExamAudioReview.css';

type Status = 'ok' | 'bad';
interface Mark {
  status?: Status;
  note?: string;
}
type Marks = Record<number, Mark>;
type Filter = 'all' | 'todo' | 'bad';
type LevelPick = 'all' | 1 | 2 | 3 | 4 | 5;

const STORAGE_KEY = 'fluent.exam-audio-review.v2';
const OLD_KEY = 'fluent.exam-audio-review.v1';

/** v1 marks were made before 81–100 moved to 101–120: move those with them. */
function migrate(v1: Marks): Marks {
  const out: Marks = {};
  for (const [k, m] of Object.entries(v1)) {
    const n = Number(k);
    out[n >= 81 && n <= 100 ? n + 20 : n] = m;
  }
  return out;
}

function loadMarks(): Marks {
  try {
    const v2 = localStorage.getItem(STORAGE_KEY);
    if (v2) return JSON.parse(v2) as Marks;
    const v1 = localStorage.getItem(OLD_KEY);
    return v1 ? migrate(JSON.parse(v1) as Marks) : {};
  } catch {
    return {};
  }
}

function saveMarks(marks: Marks) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(marks));
  } catch {
    /* private mode: marks live for this visit only */
  }
}

const wordCount = (s: string) => s.split(/\s+/u).filter(Boolean).length;

const fmt = (s: number | undefined) =>
  s === undefined ? '—' : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;

/** One message with every problem, ready to paste to Claude. */
function exportText(tests: ExamTest[], marks: Marks): string {
  const bad = tests.filter((t) => marks[t.number]?.status === 'bad');
  const ok = tests.filter((t) => marks[t.number]?.status === 'ok').length;
  const lines = bad.map((t) => `ტესტი ${t.number}: ${marks[t.number]?.note?.trim() || 'პრობლემაა'}`);
  return [`აუდიო: ${ok} კარგია, ${bad.length} პრობლემაა, ${tests.length - ok - bad.length} შეუმოწმებელი.`, ...lines].join('\n');
}

export function ExamAudioReview() {
  const [tests, setTests] = useState<ExamTest[] | null>(null);
  const [marks, setMarks] = useState<Marks>(loadMarks);
  const [filter, setFilter] = useState<Filter>('all');
  const [level, setLevel] = useState<LevelPick>(4);
  const [durations, setDurations] = useState<Record<number, number>>({});
  const [copied, setCopied] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    void Promise.all(Array.from({ length: EXAM_TEST_COUNT }, (_, i) => loadExamTest(testIdOf(i + 1)))).then(
      (all) => {
        if (alive) setTests(all.filter((t): t is ExamTest => t !== null));
      },
    );
    return () => {
      alive = false;
    };
  }, []);

  // One clip at a time: starting one pauses the others.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onPlay = (e: Event) => {
      el.querySelectorAll('audio').forEach((a) => {
        if (a !== e.target) a.pause();
      });
    };
    el.addEventListener('play', onPlay, true);
    return () => el.removeEventListener('play', onPlay, true);
  }, [tests]);

  const setMark = (n: number, patch: Mark) =>
    setMarks((prev) => {
      const next = { ...prev, [n]: { ...prev[n], ...patch } };
      saveMarks(next);
      return next;
    });

  const inScope = (t: ExamTest) => level === 'all' || t.level === level;

  // Counts follow the level picker; the copied notes always cover every clip.
  const counts = useMemo(() => {
    const scope = (tests ?? []).filter((t) => level === 'all' || t.level === level);
    const ok = scope.filter((t) => marks[t.number]?.status === 'ok').length;
    const bad = scope.filter((t) => marks[t.number]?.status === 'bad').length;
    return { all: scope.length, ok, bad, todo: scope.length - ok - bad };
  }, [tests, marks, level]);

  const shown = (t: ExamTest) =>
    inScope(t) &&
    (filter === 'all' || (filter === 'bad' ? marks[t.number]?.status === 'bad' : !marks[t.number]?.status));

  const copy = () => {
    if (!tests) return;
    navigator.clipboard.writeText(exportText(tests, marks)).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      },
      () => document.getElementById('exam-audio-export')?.focus(),
    );
  };

  return (
    <Screen>
      <Container size="md">
        <div className="fluent-audio-review">
          <header className="fluent-audio-review__head">
            <Heading level={1}>ტესტების აუდიო</Heading>
            <Text tone="muted">
              {tests?.length ?? ''} ჩანაწერი და მათი ტექსტი. ჯერ ახალი ტესტები 81–100 (გამოცდის დონე) ჩანს — სხვა დონე ზემოთ აირჩიე. მოუსმინე, მონიშნე „კარგია“ ან „პრობლემაა“ და დაწერე რა არ მოგეწონა.
              ბოლოს დააჭირე „დააკოპირე შენიშვნები“ და გამომიგზავნე.
            </Text>
            <p className="fluent-audio-review__counts" aria-live="polite">
              <span data-tone="ok">✓ {counts.ok} კარგია</span>
              <span data-tone="bad">✗ {counts.bad} პრობლემაა</span>
              <span>{counts.todo} შეუმოწმებელი</span>
            </p>
            <SegmentedControl<LevelPick>
              className="fluent-audio-review__levels"
              ariaLabel="დონე"
              value={level}
              onChange={setLevel}
              options={[
                { value: 4, label: '81–100' },
                { value: 5, label: '101–120' },
                { value: 3, label: '51–80' },
                { value: 2, label: '21–50' },
                { value: 1, label: '1–20' },
                { value: 'all', label: '1–120' },
              ]}
            />
            <SegmentedControl<Filter>
              ariaLabel="ფილტრი"
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: 'ყველა' },
                { value: 'todo', label: 'დარჩა' },
                { value: 'bad', label: 'პრობლემა' },
              ]}
            />
          </header>

          {!tests ? (
            <Stack gap={4}>
              <Skeleton shape="card" style={{ height: 220 }} />
              <Skeleton shape="card" style={{ height: 220 }} />
            </Stack>
          ) : (
            <div ref={listRef} className="fluent-audio-review__list">
              {EXAM_LEVELS.map((lv) => {
                const inLevel = tests.filter((t) => t.number >= lv.from && t.number <= lv.to && shown(t));
                if (inLevel.length === 0) return null;
                return (
                  <section key={lv.level} className="fluent-audio-review__level" aria-label={`დონე ${lv.level}`}>
                    <h2 className="fluent-audio-review__level-title">
                      დონე {lv.level} · ტესტები {lv.from}–{lv.to}
                    </h2>
                    {inLevel.map((t) => {
                      const mark = marks[t.number] ?? {};
                      const male = t.listening.voice === 'male';
                      return (
                        <article key={t.number} className="fluent-audio-review__item" data-status={mark.status}>
                          <div className="fluent-audio-review__item-head">
                            <h3 className="fluent-audio-review__item-title">ტესტი {t.number}</h3>
                            <span className="fluent-audio-review__chip" data-voice={t.listening.voice}>
                              {male ? 'კაცი · Jarnathan' : 'ქალი · Sarah'}
                            </span>
                            <span className="fluent-audio-review__len">
                              {fmt(durations[t.number])}
                              {durations[t.number] !== undefined &&
                                ` · ${Math.round(wordCount(t.listening.transcript) / (durations[t.number]! / 60))} სიტყვა/წთ`}
                            </span>
                          </div>
                          <p className="fluent-audio-review__talk" lang="en">
                            {t.listening.title}
                          </p>
                          <audio
                            className="fluent-audio-review__player"
                            controls
                            preload="metadata"
                            src={audioUrl(t.listening.audio_key)}
                            onLoadedMetadata={(e) => {
                              const d = e.currentTarget.duration;
                              if (Number.isFinite(d)) setDurations((prev) => ({ ...prev, [t.number]: d }));
                            }}
                          />
                          <div className="fluent-audio-review__script" lang="en">
                            {t.listening.transcript.split(/\n\s*\n/u).map((p, i) => (
                              <p key={i}>{p}</p>
                            ))}
                          </div>
                          <div className="fluent-audio-review__actions">
                            <Button
                              size="sm"
                              variant={mark.status === 'ok' ? 'primary' : 'secondary'}
                              onClick={() => setMark(t.number, { status: 'ok' })}
                              aria-pressed={mark.status === 'ok'}
                            >
                              ✓ კარგია
                            </Button>
                            <Button
                              size="sm"
                              variant={mark.status === 'bad' ? 'danger' : 'secondary'}
                              onClick={() => setMark(t.number, { status: 'bad' })}
                              aria-pressed={mark.status === 'bad'}
                            >
                              ✗ პრობლემაა
                            </Button>
                          </div>
                          {mark.status === 'bad' && (
                            <Textarea
                              id={`note-${t.number}`}
                              label="რა არის პრობლემა? (მაგ.: „0:42 — სიტყვა არასწორად თქვა“)"
                              rows={2}
                              value={mark.note ?? ''}
                              onChange={(e) => setMark(t.number, { note: e.target.value })}
                            />
                          )}
                        </article>
                      );
                    })}
                  </section>
                );
              })}
            </div>
          )}

          {tests && (
            <footer className="fluent-audio-review__export">
              <Heading level={2}>შენიშვნები</Heading>
              <Textarea
                id="exam-audio-export"
                label="ეს ტექსტი გამომიგზავნე"
                readOnly
                rows={Math.min(12, 2 + counts.bad)}
                value={exportText(tests, marks)}
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button onClick={copy}>{copied ? 'დაკოპირდა ✓' : 'დააკოპირე შენიშვნები'}</Button>
            </footer>
          )}
        </div>
      </Container>
    </Screen>
  );
}
